/**
 * Healin API Service
 * 
 * This service handles all API calls to the Healin backend services
 */

// API Base URLs
const USER_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod';
const MEDICATION_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/medications';
const PRESCRIPTION_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/prescriptions';
const CARE_PLAN_BUDGET_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/careplanbudget';
const HEALTH_DOCUMENTS_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/healthdocument';
const VIDEO_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/appointments/video';

// Get API key directly from environment variable
// In React, process.env values must start with REACT_APP_ to be included in the build
const API_KEY = process.env.REACT_APP_HEALIN_API_KEY;

// Common headers for all API requests
const getCommonHeaders = () => ({
  'Content-Type': 'application/json',
  'x-api-key': process.env.REACT_APP_HEALIN_API_KEY,
});

/**
 * Fetches user data for a specific user ID
 * @param {string} userId - The user ID to fetch
 * @returns {Promise<Object>} - The user data
 * @throws {Error} - If the request fails
 */
export const fetchUserData = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  // Debug API key access
  console.log('API Key from env:', process.env.REACT_APP_HEALIN_API_KEY ? 
    `${process.env.REACT_APP_HEALIN_API_KEY.substring(0, 5)}...` : 'Not found');
  
  try {
    const url = `${USER_API_URL}/users?User_ID=${encodeURIComponent(userId)}`;
    console.log('Fetching user data from:', url);
    
    // Use API key directly from environment variable
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders(),
    });

    // Handle HTTP errors
    if (!response.ok) {
      console.error('API request failed:', {
        status: response.status,
        statusText: response.statusText
      });
      
      // For testing purposes, return sample user data
      return getSampleUserData(userId);
    }

    const data = await response.json();
    console.log('Successfully fetched user data:', data);
    return data;
  } catch (error) {
    console.error('Error in fetchUserData:', error.message);
    
    // For testing purposes, return sample user data
    return getSampleUserData(userId);
  }
};

/**
 * Provides sample user data for testing
 * @param {string} userId - User ID to generate sample data for
 * @returns {Object} - Sample user data
 */
function getSampleUserData(userId) {
  console.warn(`Using sample user data for userId: ${userId}`);
  
  const sampleUsers = {
    'user1': {
      User_ID: 'user1',
      Name: 'John Smith',
      Age: 45,
      Gender: 'Male',
      BloodType: 'A+',
      Allergies: ['Penicillin', 'Sulfa drugs'],
      ChronicConditions: ['Hypertension', 'Type 2 Diabetes', 'Osteoarthritis']
    },
    'user2': {
      User_ID: 'user2',
      Name: 'Sarah Johnson',
      Age: 32,
      Gender: 'Female',
      BloodType: 'O-',
      Allergies: ['Aspirin', 'Latex'],
      ChronicConditions: ['Asthma', 'Seasonal allergies']
    },
    'user3': {
      User_ID: 'user3',
      Name: 'Robert Chen',
      Age: 68,
      Gender: 'Male',
      BloodType: 'B+',
      Allergies: ['Codeine'],
      ChronicConditions: ['Coronary artery disease', 'Hyperlipidemia', 'Hypertension']
    }
  };
  
  // Return the sample user if available, or create a generic one
  return sampleUsers[userId] || {
    User_ID: userId,
    Name: `Patient ${userId.substring(0, 8)}`,
    Age: Math.floor(Math.random() * 50) + 20,
    Gender: Math.random() > 0.5 ? 'Male' : 'Female',
    BloodType: ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'][Math.floor(Math.random() * 8)],
    Allergies: [],
    ChronicConditions: []
  };
}

/**
 * MEDICATION API FUNCTIONS
 * These functions connect to the Healin_UserMedications table
 */

/**
 * Fetches all medications for a specific user
 * @param {string} userId - The user ID to fetch medications for
 * @returns {Promise<Array>} - Array of medication objects
 * @throws {Error} - If the request fails
 */
export const fetchUserMedications = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'GET',
      headers: {
        ...getCommonHeaders(),
        'x-user-id': userId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch medications: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching medications:', error);
    throw error;
  }
};

/**
 * Fetches a specific medication by ID
 * @param {string} userId - The user ID
 * @param {string} medicationId - The medication ID to fetch
 * @returns {Promise<Object>} - The medication object
 * @throws {Error} - If the request fails
 */
export const fetchMedicationById = async (userId, medicationId) => {
  if (!userId || !medicationId) {
    throw new Error('User ID and Medication ID are required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'GET',
      headers: {
        ...getCommonHeaders(),
        'x-user-id': userId,
        'x-medication-id': medicationId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch medication: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error fetching medication:', error);
    throw error;
  }
};

/**
 * Creates a new medication
 * @param {Object} medicationData - The medication data to create
 * @param {string} medicationData.userId - The user ID (required)
 * @param {string} medicationData.medicationName - The medication name
 * @param {string} medicationData.dosage - The dosage
 * @param {string} medicationData.posology - The posology
 * @param {string} medicationData.startDate - The start date
 * @param {string} medicationData.endDate - The end date
 * @param {string} medicationData.type - The medication type
 * @returns {Promise<Object>} - The created medication object
 * @throws {Error} - If the request fails
 */
export const createMedication = async (medicationData) => {
  if (!medicationData.userId) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'POST',
      headers: getCommonHeaders(),
      body: JSON.stringify(medicationData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to create medication: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error creating medication:', error);
    throw error;
  }
};

/**
 * Updates an existing medication
 * @param {Object} medicationData - The medication data to update
 * @param {string} medicationData.userId - The user ID (required)
 * @param {string} medicationData.medicationId - The medication ID (required)
 * @returns {Promise<Object>} - The updated medication object
 * @throws {Error} - If the request fails
 */
export const updateMedication = async (medicationData) => {
  if (!medicationData.userId || !medicationData.medicationId) {
    throw new Error('User ID and Medication ID are required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'PUT',
      headers: getCommonHeaders(),
      body: JSON.stringify(medicationData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to update medication: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error updating medication:', error);
    throw error;
  }
};

/**
 * Deletes an existing medication
 * @param {string} userId - The user ID
 * @param {string} medicationId - The medication ID to delete
 * @returns {Promise<Object>} - The response object
 * @throws {Error} - If the request fails
 */
export const deleteMedication = async (userId, medicationId) => {
  if (!userId || !medicationId) {
    throw new Error('User ID and Medication ID are required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'DELETE',
      headers: getCommonHeaders(),
      body: JSON.stringify({ userId, medicationId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to delete medication: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting medication:', error);
    throw error;
  }
};

/**
 * PRESCRIPTION API FUNCTIONS
 * These functions connect to the Healin_UserPrescriptions table
 */

/**
 * Fetches all prescriptions for a specific user
 * @param {string} userId - The user ID to fetch prescriptions for
 * @returns {Promise<Array>} - Array of prescription objects
 * @throws {Error} - If the request fails
 */
export const fetchUserPrescriptions = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'GET',
      headers: {
        ...getCommonHeaders(),
        'x-user-id': userId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch prescriptions: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error('Error fetching prescriptions:', error);
    throw error;
  }
};

/**
 * Fetches a specific prescription by ID
 * @param {string} userId - The user ID
 * @param {string} requestId - The prescription ID to fetch
 * @returns {Promise<Object>} - The prescription object
 * @throws {Error} - If the request fails
 */
export const fetchPrescriptionById = async (userId, requestId) => {
  if (!userId || !requestId) {
    throw new Error('User ID and Request ID are required');
  }

  try {
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'GET',
      headers: {
        ...getCommonHeaders(),
        'x-user-id': userId,
        'x-request-id': requestId,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch prescription: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error fetching prescription:', error);
    throw error;
  }
};

/**
 * Creates a new prescription
 * @param {Object} prescriptionData - The prescription data to create
 * @param {string} prescriptionData.userId - The user ID (required)
 * @param {Array} prescriptionData.medications - List of medications (required)
 * @param {string} prescriptionData.status - Status (optional, defaults to 'Pending')
 * @param {string} prescriptionData.comments - Comments (optional)
 * @param {string} prescriptionData.source - Source (optional, defaults to 'manual')
 * @returns {Promise<Object>} - The created prescription object
 * @throws {Error} - If the request fails
 */
export const createPrescription = async (prescriptionData) => {
  if (!prescriptionData.userId || !prescriptionData.medications) {
    throw new Error('User ID and medications are required');
  }

  try {
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'POST',
      headers: getCommonHeaders(),
      body: JSON.stringify(prescriptionData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to create prescription: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error creating prescription:', error);
    throw error;
  }
};

/**
 * Updates an existing prescription
 * @param {Object} prescriptionData - The prescription data to update
 * @param {string} prescriptionData.userId - The user ID (required)
 * @param {string} prescriptionData.requestId - The request ID (required)
 * @param {string} prescriptionData.status - Status (optional)
 * @param {string} prescriptionData.comments - Comments (optional)
 * @param {string} prescriptionData.source - Source (optional)
 * @returns {Promise<Object>} - The updated prescription object
 * @throws {Error} - If the request fails
 */
export const updatePrescription = async (prescriptionData) => {
  if (!prescriptionData.userId || !prescriptionData.requestId) {
    throw new Error('User ID and Request ID are required');
  }

  try {
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'PUT',
      headers: getCommonHeaders(),
      body: JSON.stringify(prescriptionData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to update prescription: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error updating prescription:', error);
    throw error;
  }
};

/**
 * Deletes an existing prescription
 * @param {string} userId - The user ID
 * @param {string} requestId - The request ID to delete
 * @returns {Promise<Object>} - The response object
 * @throws {Error} - If the request fails
 */
export const deletePrescription = async (userId, requestId) => {
  if (!userId || !requestId) {
    throw new Error('User ID and Request ID are required');
  }

  try {
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'DELETE',
      headers: getCommonHeaders(),
      body: JSON.stringify({ userId, requestId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to delete prescription: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error deleting prescription:', error);
    throw error;
  }
};

/**
 * Claims a prescription request by assigning it to a doctor
 * @param {Object} claimData - The claim data
 * @param {string} claimData.userId - The patient's user ID
 * @param {string} claimData.requestId - The prescription request ID
 * @param {string} claimData.assignedDoctorId - The doctor's user ID claiming the prescription
 * @param {string} [claimData.comments] - Optional comments from the doctor
 * @returns {Promise<Object>} - The updated prescription object
 * @throws {Error} - If the request fails
 */
export const claimPrescription = async (claimData) => {
  const { userId, requestId, assignedDoctorId, comments } = claimData;
  
  if (!userId || !requestId || !assignedDoctorId) {
    throw new Error('User ID, Request ID, and Assigned Doctor ID are required');
  }

  try {
    console.log('Claiming prescription:', { userId, requestId, assignedDoctorId });
    
    const response = await fetch(PRESCRIPTION_API_URL, {
      method: 'PUT',
      headers: getCommonHeaders(),
      body: JSON.stringify({
        userId,
        requestId,
        status: 'CLAIMED',
        assignedDoctorId,
        ...(comments && { comments })
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error claiming prescription:', errorData);
      throw new Error(
        errorData?.message || `Failed to claim prescription: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully claimed prescription:', data);
    return data;
  } catch (error) {
    console.error('Error claiming prescription:', error);
    throw error;
  }
};

/**
 * Fetches prescriptions claimed by a specific doctor
 * @param {string} doctorId - The doctor's user ID
 * @param {Array<string>} [statuses] - Optional array of statuses to filter by (e.g., ['CLAIMED'])
 * @param {boolean} [includeMedicationData=false] - Whether to include detailed medication data
 * @param {boolean} [includeUserData=false] - Whether to include detailed user data
 * @param {string} [lastKey] - Pagination key for fetching next page
 * @returns {Promise<Object>} - Object containing items array and lastKey for pagination
 * @throws {Error} - If the request fails
 */
export const fetchPrescriptionsByDoctor = async (doctorId, statuses = [], includeMedicationData = false, includeUserData = false, lastKey = null) => {
  if (!doctorId) {
    throw new Error('Doctor ID is required');
  }

  try {
    // Build query parameters
    const queryParams = new URLSearchParams({
      assignedDoctorId: doctorId,
      includeMedicationData: includeMedicationData.toString(),
      includeUserData: includeUserData.toString()
    });

    if (statuses && statuses.length > 0) {
      queryParams.append('statuses', statuses.join(','));
    }

    if (lastKey) {
      queryParams.append('lastKey', lastKey);
    }

    const url = `${PRESCRIPTION_API_URL}?${queryParams.toString()}`;
    console.log('Fetching prescriptions for doctor:', { doctorId, statuses, includeMedicationData, includeUserData, url });
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error fetching doctor prescriptions:', errorData);
      throw new Error(
        errorData?.message || `Failed to fetch doctor prescriptions: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched doctor prescriptions:', data);
    return {
      items: data.items || [],
      lastKey: data.lastKey || null
    };
  } catch (error) {
    console.error('Error fetching prescriptions by doctor:', error);
    throw error;
  }
};

/**
 * CARE PLAN BUDGET API FUNCTIONS
 * These functions connect to the PlanOfCare-kn7rgyhpfjcgbdg22no2r3rui4-staging table
 */

/**
 * Fetches the most recent care plan budget for a given profile ID
 * @param {string} profileId - The profile ID to fetch care plan budget for
 * @returns {Promise<Object>} - The care plan budget object
 * @throws {Error} - If the request fails
 */
export const fetchCarePlanBudget = async (profileId) => {
  if (!profileId) {
    throw new Error('Profile ID is required');
  }

  try {
    const url = `${CARE_PLAN_BUDGET_URL}?profileID=${encodeURIComponent(profileId)}`;
    console.log('Fetching care plan budget from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error response:', errorData);
      
      if (response.status === 404) {
        throw new Error(`No care plan budget found for profile ID ${profileId}`);
      }
      
      throw new Error(
        errorData?.error || `Failed to fetch care plan budget: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched care plan budget:', data);
    return data;
  } catch (error) {
    console.error('Error fetching care plan budget:', error.message);
    throw error;
  }
};

/**
 * Fetches plan of care data for a specific user ID
 * @param {string} userId - The user ID to fetch plan of care for
 * @returns {Promise<Object>} - The plan of care data
 * @throws {Error} - If the request fails
 */
export const fetchPlanOfCareByUserId = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const url = `${CARE_PLAN_BUDGET_URL}?userId=${encodeURIComponent(userId)}`;
    console.log('Fetching plan of care by userId from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error response:', errorData);
      
      if (response.status === 404) {
        throw new Error(`No plan of care found for user ID ${userId}`);
      }
      
      throw new Error(
        errorData?.error || `Failed to fetch plan of care: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched plan of care:', data);
    return data;
  } catch (error) {
    console.error('Error fetching plan of care by userId:', error.message);
    throw error;
  }
};

/**
 * Fetches health documents for a specific user
 * @param {string} userId - The user ID to fetch documents for
 * @param {string} identityId - The identity ID (profileID from plan of care)
 * @returns {Promise<Object>} - The health documents data
 * @throws {Error} - If the request fails
 */
export const fetchHealthDocuments = async (userId, identityId) => {
  if (!userId || !identityId) {
    throw new Error('User ID and Identity ID are required');
  }

  try {
    const url = `${HEALTH_DOCUMENTS_API_URL}?userID=${encodeURIComponent(userId)}&identityID=${encodeURIComponent(identityId)}`;
    console.log('Fetching health documents from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error('Error response:', errorData);
      
      if (response.status === 404) {
        throw new Error(`No health documents found for user ID ${userId}`);
      }
      
      throw new Error(
        errorData?.error || `Failed to fetch health documents: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    console.log('Successfully fetched health documents:', data);
    return data;
  } catch (error) {
    console.error('Error fetching health documents:', error.message);
    throw error;
  }
};

/**
 * Fetches all prescription requests from all users
 * This endpoint is used by healthcare providers to view available prescription requests
 * @returns {Promise<Array>} - Array of prescription request objects
 * @throws {Error} - If the request fails
 */
export const fetchAllPrescriptionRequests = async () => {
  try {
    // The endpoint uses the all=true query parameter, not a /requests path
    const url = `${PRESCRIPTION_API_URL}?all=true`;
    console.log('Fetching all prescription requests from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: getCommonHeaders()
    });

    if (!response.ok) {
      console.error('Error response:', response.status, response.statusText);
      
      // For testing purposes, return sample data if API is not yet ready
      console.warn('Using sample prescriptions data for testing');
      return getSamplePrescriptions();
    }

    const data = await response.json();
    console.log('Successfully fetched prescription requests:', data);
    
    if (!data.items || data.items.length === 0) {
      console.warn('API returned empty data, using sample data for testing');
      return getSamplePrescriptions();
    }
    
    return data.items || [];
  } catch (error) {
    console.error('Error fetching prescription requests:', error.message);
    
    // For testing purposes, return sample data if API is not yet ready
    console.warn('Using sample prescriptions data for testing');
    return getSamplePrescriptions();
  }
};

/**
 * Provides sample prescription data for testing when API is not available
 * @returns {Array} - Sample prescription data
 */
function getSamplePrescriptions() {
  return [
    {
      userId: 'user1', 
      requestId: 'req1',
      status: 'Pending',
      requestedAt: new Date().toISOString(),
      medications: [
        {
          id: 'med1',
          name: 'Metformin',
          dosage: '500mg',
          frequency: 'Twice daily',
          condition: 'Type 2 Diabetes'
        },
        {
          id: 'med2',
          name: 'Lisinopril',
          dosage: '10mg',
          frequency: 'Once daily',
          condition: 'Hypertension'
        }
      ],
      comments: 'Patient requested refill of diabetes medication'
    },
    {
      userId: 'user2',
      requestId: 'req2',
      status: 'Pending',
      requestedAt: new Date(Date.now() - 86400000).toISOString(),
      medications: [
        {
          id: 'med3',
          name: 'Albuterol Inhaler',
          dosage: '90mcg',
          frequency: 'As needed',
          condition: 'Asthma'
        }
      ],
      comments: 'Patient experiencing more frequent asthma attacks'
    },
    {
      userId: 'user3',
      requestId: 'req3',
      status: 'Claimed',
      requestedAt: new Date(Date.now() - 172800000).toISOString(),
      doctorId: 'doctor1',
      medications: [
        {
          id: 'med4',
          name: 'Atorvastatin',
          dosage: '40mg',
          frequency: 'Once daily at bedtime',
          condition: 'Hyperlipidemia'
        },
        {
          id: 'med5',
          name: 'Aspirin',
          dosage: '81mg',
          frequency: 'Once daily',
          condition: 'Heart disease prevention'
        }
      ],
      comments: 'Patient needs refill of heart medications'
    }
  ];
}

/**
 * Fetches medication details for a specific medication ID
 * @param {string} userId - The user ID
 * @param {string} medicationId - The medication ID
 * @returns {Promise<Object>} - The medication details
 * @throws {Error} - If the request fails
 */
export const fetchMedicationDetails = async (userId, medicationId) => {
  if (!userId || !medicationId) {
    throw new Error('User ID and Medication ID are required');
  }

  try {
    const response = await fetch(MEDICATION_API_URL, {
      method: 'GET',
      headers: {
        ...getCommonHeaders(),
        'x-user-id': userId,
        'x-medication-id': medicationId
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.warn(`Medication with ID ${medicationId} not found for user ${userId}`);
        // Return a placeholder if the medication is not found
        return {
          medicationId: medicationId,
          medicationName: 'Unknown Medication',
          dosage: 'N/A',
          posology: 'N/A'
        };
      }
      
      const errorData = await response.json().catch(() => null);
      throw new Error(
        errorData?.message || `Failed to fetch medication details: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data.item;
  } catch (error) {
    console.error('Error fetching medication details:', error);
    // Return a placeholder in case of error to avoid breaking the UI
    return {
      medicationId: medicationId,
      medicationName: 'Error fetching medication',
      dosage: 'N/A',
      posology: 'N/A'
    };
  }
};

/**
 * Enriches prescription data with full medication details
 * @param {Object} prescription - The prescription object
 * @returns {Promise<Object>} - Enriched prescription with full medication details
 */
export const enrichPrescriptionWithMedicationDetails = async (prescription) => {
  if (!prescription) return prescription;
  
  // If the prescription doesn't have medications or they're already enriched, return as is
  if (!prescription.medications || 
      (prescription.medications.length > 0 && prescription.medications[0].medicationName)) {
    return prescription;
  }
  
  try {
    // For each medication ID in the prescription, fetch the full details
    const enrichedMedications = await Promise.all(
      prescription.medications.map(async (med) => {
        // If it's already an object with details, return as is
        if (typeof med === 'object' && med.medicationName) {
          return med;
        }
        
        // If it's just a medication ID or an object with only medicationId
        const medicationId = typeof med === 'string' ? med : med.medicationId;
        
        if (!medicationId) {
          return {
            medicationId: 'unknown',
            medicationName: 'Unknown Medication',
            dosage: 'N/A',
            posology: 'N/A'
          };
        }
        
        // Fetch the medication details
        const medicationDetails = await fetchMedicationDetails(prescription.userId, medicationId);
        return medicationDetails;
      })
    );
    
    // Return the enriched prescription
    return {
      ...prescription,
      medications: enrichedMedications
    };
  } catch (error) {
    console.error('Error enriching prescription with medication details:', error);
    return prescription;
  }
};

// New batch prescription fetching with pagination support
export const fetchBatch = async ({ statuses = [], includeMedicationData = false, lastKey = null } = {}) => {
  try {
    const url = new URL(`${PRESCRIPTION_API_URL}`);
    url.searchParams.set("all", "true");
    
    if (statuses.length > 0) {
      // Send statuses as-is since database stores them in proper case format
      url.searchParams.set("statuses", statuses.join(","));
    }
    
    if (includeMedicationData) {
      url.searchParams.set("includeMedicationData", "true");
    }
    
    if (lastKey) {
      url.searchParams.set("lastKey", lastKey);
    }

    console.log('=== API REQUEST DEBUG ===');
    console.log('Full URL:', url.toString());
    console.log('Query Parameters:', {
      all: 'true',
      statuses: statuses.join(","),
      includeMedicationData: includeMedicationData.toString(),
      lastKey: lastKey
    });

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY
      }
    });

    console.log('=== API RESPONSE DEBUG ===');
    console.log('Response Status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error Response Text:', errorText);
      
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText };
      }
      
      throw new Error(`HTTP ${response.status}: ${errorData.message || 'Failed to fetch prescriptions'}`);
    }

    const responseText = await response.text();
    console.log('Raw Response Text:', responseText.substring(0, 500) + '...');
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (e) {
      console.error('Failed to parse JSON response:', e);
      throw new Error('Invalid JSON response from server');
    }

    console.log('Parsed Response Data - Items count:', data.items?.length || 0);
    
    if (data.items && data.items.length > 0) {
      console.log('Sample item structure:', {
        requestId: data.items[0].requestId,
        status: data.items[0].status,
        medications: data.items[0].medications?.length || 0,
        hasMedicationsDetail: !!data.items[0].medicationsDetail
      });
    }

    return {
      items: data.items || [],
      lastKey: data.lastKey
    };
  } catch (error) {
    console.error('=== API ERROR DEBUG ===');
    console.error('Error details:', error);
    throw error;
  }
};

// Helper function to fetch all prescriptions with pagination
export const fetchAllPrescriptions = async (options = {}) => {
  let lastKey = undefined;
  const all = [];

  do {
    const { items, lastKey: next } = await fetchBatch({ ...options, lastKey });
    all.push(...items);
    lastKey = next;
  } while (lastKey);

  return all;
};

// Procedure Requests API functions
export const fetchAllProcedureRequests = async (options = {}) => {
  try {
    const { statuses = [], includePlanData = false, includeUserData = false } = options;
    
    const params = new URLSearchParams();
    params.append('all', 'true');
    
    if (statuses.length > 0) {
      params.append('statuses', statuses.join(','));
    }
    
    // Add enrichment parameters
    if (includePlanData) {
      params.append('includePlanData', 'true');
    }
    
    if (includeUserData) {
      params.append('includeUserData', 'true');
    }
    
    const url = `${CARE_PLAN_BUDGET_URL}/procedure-requests?${params.toString()}`;
    console.log('Fetching procedure requests from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Procedure requests response:', data);
    
    return data.items || [];
  } catch (error) {
    console.error('Error fetching procedure requests:', error);
    throw error;
  }
};

export const fetchProcedureRequestsByDoctor = async (doctorId, statuses = [], includePlanData = false, includeUserData = false) => {
  try {
    const params = new URLSearchParams();
    params.append('assignedDoctorId', doctorId);
    
    if (statuses.length > 0) {
      params.append('statuses', statuses.join(','));
    }
    
    // Add enrichment parameters
    if (includePlanData) {
      params.append('includePlanData', 'true');
    }
    
    if (includeUserData) {
      params.append('includeUserData', 'true');
    }
    
    const url = `${CARE_PLAN_BUDGET_URL}/procedure-requests?${params.toString()}`;
    console.log('Fetching doctor procedure requests from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Doctor procedure requests response:', data);
    
    return data;
  } catch (error) {
    console.error('Error fetching doctor procedure requests:', error);
    throw error;
  }
};

export const claimProcedureRequest = async (claimData) => {
  try {
    const { userId, requestId, assignedDoctorId } = claimData;
    
    const payload = {
      userId,
      requestId,
      status: 'CLAIMED',
      assignedDoctorId,
      assignedAt: new Date().toISOString()
    };
    
    console.log('Claiming procedure request:', payload);
    
    const response = await fetch(`${CARE_PLAN_BUDGET_URL}/procedure-requests`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Procedure request claimed successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error claiming procedure request:', error);
    throw error;
  }
};

export const updateProcedureRequest = async (updateData) => {
  try {
    const payload = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    console.log('Updating procedure request:', payload);
    
    const response = await fetch(`${CARE_PLAN_BUDGET_URL}/procedure-requests`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Procedure request updated successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error updating procedure request:', error);
    throw error;
  }
};

// Appointments API functions
const APPOINTMENTS_API_URL = 'https://8uzk12sdzc.execute-api.eu-west-1.amazonaws.com/prod/appointments';

export const fetchAllAppointments = async (options = {}) => {
  try {
    const { statuses = [], includeUserData = false, includePlanData = false } = options;
    
    const params = new URLSearchParams();
    params.append('all', 'true');
    
    if (statuses.length > 0) {
      params.append('statuses', statuses.join(','));
    }
    
    // Add enrichment parameters
    if (includeUserData) {
      params.append('includeUserData', 'true');
    }
    
    if (includePlanData) {
      params.append('includePlanData', 'true');
    }
    
    const url = `${APPOINTMENTS_API_URL}?${params.toString()}`;
    console.log('Fetching appointments from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Appointments response:', data);
    
    return data.items || [];
  } catch (error) {
    console.error('Error fetching appointments:', error);
    throw error;
  }
};

export const fetchAppointmentsByDoctor = async (doctorId, statuses = [], includeUserData = false, includePlanData = false) => {
  try {
    const params = new URLSearchParams();
    params.append('assignedDoctorId', doctorId);
    
    if (statuses.length > 0) {
      params.append('statuses', statuses.join(','));
    }
    
    // Add enrichment parameters
    if (includeUserData) {
      params.append('includeUserData', 'true');
    }
    
    if (includePlanData) {
      params.append('includePlanData', 'true');
    }
    
    const url = `${APPOINTMENTS_API_URL}?${params.toString()}`;
    console.log('Fetching doctor appointments from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Doctor appointments response:', data);
    
    return data;
  } catch (error) {
    console.error('Error fetching doctor appointments:', error);
    throw error;
  }
};

export const updateAppointment = async (updateData) => {
  try {
    const payload = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    console.log('Updating appointment:', payload);
    
    const response = await fetch(APPOINTMENTS_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Appointment updated successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error updating appointment:', error);
    throw error;
  }
};

export const confirmAppointment = async (confirmData) => {
  try {
    const { userId, requestId, assignedDoctorId, confirmedSlot } = confirmData;
    
    const payload = {
      userId,
      requestId,
      status: 'CONFIRMED',
      assignedDoctorId,
      confirmedSlot,
      confirmedAt: new Date().toISOString()
    };
    
    console.log('Confirming appointment:', payload);
    
    const response = await fetch(APPOINTMENTS_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Appointment confirmed successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error confirming appointment:', error);
    throw error;
  }
};

export const fetchAppointmentById = async (userId, requestId, includeUserData = true, includePlanData = true) => {
  try {
    const params = new URLSearchParams();
    params.append('assignedDoctorId', userId); // Using userId as doctor ID for fetching
    
    if (includeUserData) {
      params.append('includeUserData', 'true');
    }
    
    if (includePlanData) {
      params.append('includePlanData', 'true');
    }
    
    const url = `${APPOINTMENTS_API_URL}?${params.toString()}`;
    console.log('Fetching appointment details from:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Appointment details response:', data);
    
    // Find the specific appointment by requestId
    const appointment = data.items?.find(item => item.requestId === requestId);
    
    if (!appointment) {
      throw new Error('Appointment not found');
    }
    
    return appointment;
  } catch (error) {
    console.error('Error fetching appointment details:', error);
    throw error;
  }
};

export const startAppointment = async (startData) => {
  try {
    const payload = {
      ...startData,
      status: 'ONGOING',
      startedAt: new Date().toISOString()
    };
    
    console.log('Starting appointment:', payload);
    
    const response = await fetch(APPOINTMENTS_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Appointment started successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error starting appointment:', error);
    throw error;
  }
};

export const completeAppointment = async (completeData) => {
  try {
    const payload = {
      ...completeData,
      status: 'COMPLETED',
      completedAt: new Date().toISOString()
    };
    
    console.log('Completing appointment:', payload);
    
    const response = await fetch(APPOINTMENTS_API_URL, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Appointment completed successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error completing appointment:', error);
    throw error;
  }
};

/**
 * VIDEO API FUNCTIONS
 * These functions handle Twilio Video integration for appointments
 */

/**
 * Starts a video call for an appointment
 * @param {Object} startData - The data needed to start a video call
 * @param {string} startData.userId - The patient's user ID
 * @param {string} startData.requestId - The appointment request ID
 * @param {string} startData.assignedDoctorId - The doctor's ID
 * @returns {Promise<Object>} - Response containing roomSid and status
 * @throws {Error} - If the request fails
 */
export const startVideoCall = async (startData) => {
  try {
    const { userId, requestId, assignedDoctorId } = startData;
    
    if (!userId || !requestId || !assignedDoctorId) {
      throw new Error('userId, requestId, and assignedDoctorId are required');
    }
    
    const payload = {
      userId,
      requestId,
      assignedDoctorId
    };
    
    console.log('Starting video call:', payload);
    
    const response = await fetch(`${VIDEO_API_URL}/start`, {
      method: 'POST',
      headers: getCommonHeaders(),
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Video call started successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error starting video call:', error);
    throw error;
  }
};

/**
 * Gets a video access token for joining a call
 * @param {Object} tokenData - The data needed to get a token
 * @param {string} tokenData.userId - The user ID (patient or doctor)
 * @param {string} tokenData.requestId - The appointment request ID
 * @returns {Promise<Object>} - Response containing token, roomSid, and expiresIn
 * @throws {Error} - If the request fails
 */
export const getVideoToken = async (tokenData) => {
  try {
    const { userId, requestId } = tokenData;
    
    if (!userId || !requestId) {
      throw new Error('userId and requestId are required');
    }
    
    const params = new URLSearchParams({
      userId,
      requestId
    });
    
    console.log('Getting video token for:', { userId, requestId });
    
    const response = await fetch(`${VIDEO_API_URL}/token?${params.toString()}`, {
      method: 'GET',
      headers: getCommonHeaders()
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Video token retrieved successfully');
    
    return result;
  } catch (error) {
    console.error('Error getting video token:', error);
    throw error;
  }
};

/**
 * Ends a video call for an appointment
 * @param {Object} endData - The data needed to end a video call
 * @param {string} endData.userId - The patient's user ID
 * @param {string} endData.requestId - The appointment request ID
 * @param {string} endData.assignedDoctorId - The doctor's ID
 * @returns {Promise<Object>} - Response confirming the call was ended
 * @throws {Error} - If the request fails
 */
export const endVideoCall = async (endData) => {
  try {
    const { userId, requestId, assignedDoctorId } = endData;
    
    if (!userId || !requestId || !assignedDoctorId) {
      throw new Error('userId, requestId, and assignedDoctorId are required');
    }
    
    const payload = {
      userId,
      requestId,
      assignedDoctorId
    };
    
    console.log('Ending video call:', payload);
    
    const response = await fetch(`${VIDEO_API_URL}/end`, {
      method: 'PUT',
      headers: getCommonHeaders(),
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    console.log('Video call ended successfully:', result);
    
    return result;
  } catch (error) {
    console.error('Error ending video call:', error);
    throw error;
  }
}; 